import { Link } from "wouter";
import { useQuery } from "@tanstack/react-query";

export function SiteFooter() {
  const { data: publicSettings } = useQuery<any[]>({
    queryKey: ["/api/settings/public"],
    queryFn: async () => { const r = await fetch("/api/settings/public"); return r.json(); },
    staleTime: 10 * 60 * 1000,
  });
  // Guard against a non-array response (e.g. a 500 error object) so a settings
  // failure can never crash the whole app with "x.find is not a function".
  const settingsArr = Array.isArray(publicSettings) ? publicSettings : [];
  const companyInfo = settingsArr.find((s: any) => s.key === "company_info")?.value || "";
  const paymentProvider = settingsArr.find((s: any) => s.key === "payment_provider")?.value || "verotel";

  return (
    <footer className="bg-gradient-to-b from-transparent via-foreground/[0.03] to-foreground/[0.05] pb-24 lg:pb-0">
      <div className="mx-auto max-w-6xl px-4 py-10 md:py-12">
        <div className="flex flex-col items-center text-center md:flex-row md:items-start md:justify-between md:text-left">
          <div className="mb-6 md:mb-0">
            <Link href="/" className="inline-flex items-center gap-2">
              <img src="/assets/logo.png" alt="CercAmanti" className="contain w-32 md:w-40" />
              <span className="sr-only">CercAmanti — Connessioni per adulti</span>
            </Link>
            <p className="mt-2 text-sm text-muted-foreground">
              Connessioni che fanno bene. Solo per adulti.
            </p>
            <p className="mt-4 text-sm text-muted-foreground max-w-xs">
              CercAmanti è un’esperienza immersiva tutta italiana di intrattenimento conversazionale con Amanti AI con cui parlare liberamente, sentirsi ascoltati e lasciarsi andare senza giudizi.
            </p>
          </div>
          {/* Due raggruppamenti su mobile, tre colonne da md in su.
              Su mobile le voci legali stanno tutte a sinistra e il resto a
              destra: con grid-cols-2 e tre liste, la terza finiva a capo da
              sola sotto le altre due, spaiata. Le liste desktop restano quelle
              di prima, quindi da md in su il footer non cambia. */}
          <div className="grid grid-cols-2 md:grid-cols-3 gap-x-12 lg:gap-x-16 gap-y-2 text-sm text-muted-foreground text-center md:mt-9 md:text-left">
            {/* Colonna legale su mobile; su md+ torna a essere la prima delle
                tre liste storiche e cede le ultime due voci alla colonna
                centrale (nascoste qui da md in su). */}
            <ul className="space-y-2">
              <li>
                <Link href="/termini" className="hover:text-primary">
                  Termini &amp; condizioni
                </Link>
              </li>
              <li>
                <Link href="/privacy" className="hover:text-primary">
                  Privacy Policy
                </Link>
              </li>
              <li>
                <Link href="/cookie-policy" className="hover:text-primary">
                  Cookie
                </Link>
              </li>
              {/* Complaints Policy: link separato e distinto dai Termini,
                  prominente nel footer persistente di ogni pagina (compliance). */}
              <li className="md:hidden">
                <Link href="/reclami" className="hover:text-primary">
                  Politica Reclami
                </Link>
              </li>
              <li className="md:hidden">
                <Link href="/termini-e-policy" className="hover:text-primary">
                  Policy &amp; Regolamenti
                </Link>
              </li>
            </ul>
            {/* Tutto il resto. Su mobile e' la seconda e ultima colonna, quindi
                assorbe anche FAQ/Affiliati/Contatti della terza lista. */}
            <ul className="space-y-2">
              <li>
                <Link href="/amanti-ai" className="hover:text-primary">
                  Amanti AI
                </Link>
              </li>
              <li className="hidden md:block">
                <Link href="/reclami" className="hover:text-primary">
                  Politica Reclami
                </Link>
              </li>
              <li className="hidden md:block">
                <Link href="/termini-e-policy" className="hover:text-primary">
                  Policy &amp; Regolamenti
                </Link>
              </li>
              <li className="md:hidden">
                {/* Help center esterno (Zendesk): non e' una rotta dell'app,
                    quindi <a> e non <Link>, e si apre in una nuova scheda. */}
                <a
                  href="https://almavirtualservicesltd.zendesk.com/hc/it"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="hover:text-primary"
                >
                  FAQ
                </a>
              </li>
              <li className="md:hidden">
                <Link href="/affiliate-apply" className="hover:text-primary">
                  Affiliati
                </Link>
              </li>
              <li className="md:hidden">
                <Link href="/contatti" className="hover:text-primary">
                  Contatti
                </Link>
              </li>
            </ul>
            {/* Terza colonna: esiste solo da md in su, su mobile le sue voci
                sono gia' rese dalla lista qui sopra. */}
            <ul className="hidden md:block space-y-2">
              <li>
                <a
                  href="https://cercamanti.zendesk.com/hc/it"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="hover:text-primary"
                >
                  FAQ
                </a>
              </li>
              <li>
                <Link href="/affiliate-apply" className="hover:text-primary">
                  Affiliati
                </Link>
              </li>
              <li>
                <Link href="/contatti" className="hover:text-primary">
                  Contatti
                </Link>
              </li>
            </ul>
          </div>
        </div>
      </div>
      <div className="py-4">
        {companyInfo && (
          <p className="mx-auto max-w-6xl px-4 text-center text-[11px] text-muted-foreground mb-1">
            {companyInfo}
          </p>
        )}
        <p className="mx-auto max-w-6xl px-4 text-center text-xs text-muted-foreground">
          © {new Date().getFullYear()} CercAmanti. Tutti i diritti riservati.
        </p>
        {(paymentProvider === "verotel" || paymentProvider === "ccbill" || paymentProvider === "all") && (
          <div className="mx-auto max-w-6xl px-4 mt-3 flex flex-col items-center gap-2">
            <div className="flex items-center gap-3">
              <img src="/assets/mastercard.svg" alt="Mastercard" className="h-6 object-contain" />
              <img src="/assets/visa.png" alt="Visa" className="h-6 object-contain" />
            </div>
          </div>
        )}
      </div>
    </footer>
  );
}